import React, { useState, useEffect } from 'react';
import HowItWorks from '../components/HowItWorks';
import FeaturedFreelancers from '../components/FeaturedFreelancers';
import { Row, Col, Card, Input, Spin, Button } from 'antd';
import { SearchOutlined, ArrowLeftOutlined, ArrowRightOutlined, GlobalOutlined, SafetyOutlined, ThunderboltOutlined, StarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import { useAuth } from '../AuthContext';
import { getCategoriesCached } from '../utils/categoriesCache';
import SEO from '../components/SEO';
import { CategoryIcon } from '../utils/categoryIcons';
import { HeroIllustration } from '../components/HeroIllustration';

const t = {
  ar: {
    heroTitle: 'وِجهتك الأولى',
    heroHighlight: 'للعمل الحر في العالم العربي',
    heroSub: 'تواصل مع أفضل المستقلين العرب واحصل على خدمات احترافية بأسعار منافسة',
    search: 'ابحث عن خدمة... مثال: تصميم شعار، تطوير موقع، ترجمة',
    categories: 'تصفح التخصصات',
    categoriesSub: 'اختر من بين عشرات التخصصات والمجالات',
    viewAll: 'عرض كل التصنيفات',
    why: 'لماذا لانسر سيتي؟',
    whySub: 'نوفر لك بيئة عمل حر آمنة واحترافية',
    whyItems: [
      { Icon: GlobalOutlined, title: 'عربي أولاً', desc: 'واجهة عربية بالكامل مع دعم كامل للغة العربية والسوق العربي' },
      { Icon: SafetyOutlined, title: 'دفع آمن ومحمي', desc: 'نظام حماية مالية متكامل يضمن حقوق المستقل والعميل' },
      { Icon: ThunderboltOutlined, title: 'سرعة في التنفيذ', desc: 'آلاف المستقلين المحترفين جاهزين لبدء العمل على مشروعك فوراً' },
      { Icon: StarOutlined, title: 'جودة مضمونة', desc: 'نظام تقييم شفاف ومراجعات حقيقية تضمن لك أفضل النتائج' },
    ],
    tags: ['تصميم شعار', 'تطوير مواقع', 'ترجمة', 'تسويق رقمي', 'مونتاج فيديو', 'كتابة محتوى'],
    stats: [
      { number: '+1,000', label: 'مستقل محترف' },
      { number: '+500', label: 'خدمة متنوعة' },
      { number: '+50', label: 'تخصص' },
      { number: '24/7', label: 'دعم فني' },
    ],
    cta: 'ابدأ مشروعك الآن',
    ctaTitle: 'جاهز تبدأ؟',
    ctaSub: 'انضم لآلاف المستقلين والعملاء في أكبر منصة عمل حر عربية',
    ctaBtn: 'سجّل مجاناً',
  },
  en: {
    heroTitle: 'Your #1 Destination',
    heroHighlight: 'for Arab Freelancing',
    heroSub: 'Connect with top Arab freelancers and get professional services at competitive prices',
    search: 'Search services... e.g. logo design, web development, translation',
    categories: 'Browse Specialties',
    categoriesSub: 'Choose from dozens of specialties and fields',
    viewAll: 'View all categories',
    why: 'Why LancerCity?',
    whySub: 'We provide a safe and professional freelancing environment',
    whyItems: [
      { Icon: GlobalOutlined, title: 'Arabic First', desc: 'Full Arabic interface with complete support for the Arabic language and market' },
      { Icon: SafetyOutlined, title: 'Secure Payments', desc: 'Integrated financial protection system that guarantees rights of freelancers and clients' },
      { Icon: ThunderboltOutlined, title: 'Fast Delivery', desc: 'Thousands of professional freelancers ready to start working on your project immediately' },
      { Icon: StarOutlined, title: 'Guaranteed Quality', desc: 'Transparent rating system and real reviews ensure the best results' },
    ],
    tags: ['Logo Design', 'Web Development', 'Translation', 'Digital Marketing', 'Video Editing', 'Content Writing'],
    stats: [
      { number: '+1,000', label: 'Pro Freelancers' },
      { number: '+500', label: 'Services' },
      { number: '+50', label: 'Specialties' },
      { number: '24/7', label: 'Support' },
    ],
    cta: 'Start Your Project',
    ctaTitle: 'Ready to start?',
    ctaSub: 'Join thousands of freelancers and clients on the largest Arab freelancing platform',
    ctaBtn: 'Register Free',
  },
};

// De-duplicate parent categories by lowercase English name; keep the one
// with the most children so we show the populated branch.
function dedupeParents(cats) {
  if (!Array.isArray(cats)) return [];
  const byName = new Map();
  for (const p of cats) {
    if (p.parent) continue; // only parents
    const key = (p.name_en || p.name_ar || '').toLowerCase().replace(/\s+&\s+/g, ' & ').trim();
    if (!key) continue;
    const existing = byName.get(key);
    const score = (p.children || []).length;
    if (!existing || score > existing.score) byName.set(key, { cat: p, score });
  }
  return Array.from(byName.values()).map(v => v.cat);
}

const Home = ({ lang }) => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  const tx = t[lang] || t.ar;

  useEffect(() => {
    API.get('/public/stats/').then((r) => setStats(r.data)).catch(() => {});
    getCategoriesCached().then(res => {
      const data = res.data.results || res.data || [];
      setCategories(dedupeParents(data));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSearch = () => { if (search.trim()) navigate(`/gigs?search=${search}`); };

  return (
    <div>
      <SEO
        title=""
        description="لانسر سيتي — منصة العمل الحر الأولى في العالم العربي. تواصل مع أفضل المستقلين العرب: مصممين، مبرمجين، كتّاب، مترجمين ومسوقين. نظام ضمان آمن وأسعار منافسة."
        path="/"
        lang={lang || 'ar'}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "لانسر سيتي — الرئيسية",
          "url": "https://lancercity.com/",
          "inLanguage": ["ar", "en"],
        }}
      />
      
      {/* ══ Hero ══ */}
      <div className="hero-section">
        <div className="hero-glow"></div>
        <div className="hero-content">
          <h1>
            {tx.heroTitle}<br/>
            <span className="hero-highlight">{tx.heroHighlight}</span>
          </h1>
          <p>{tx.heroSub}</p>
          <div className="hero-bonus">🎁 {lang === 'ar' ? 'سجّل الآن واحصل على هدية ترحيب $30' : 'Sign up now and get a $30 welcome bonus'} <span className="hero-bonus-terms">{lang === 'ar' ? '(تُطبَّق الشروط)' : '(terms apply)'}</span></div>
          <div className="hero-search">
            <Input size="large" placeholder={tx.search} prefix={<SearchOutlined />}
              value={search} onChange={e => setSearch(e.target.value)}
              onPressEnter={handleSearch} />
          </div>
          <div className="hero-tags">
            {tx.tags.map((tag, i) => (
              <span key={i} className="hero-tag" onClick={() => navigate(`/gigs?search=${tag}`)}>{tag}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ══ Stats ══ */}
      <div className="stats-bar">
        {tx.stats.map((s, i) => (
          <div className="stat-item" key={i}>
            <div className="stat-number">{s.number}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ══ Categories ══ */}
      <div className="content-container">
        <div className="section-header">
          <h2 className="section-title">{tx.categories}</h2>
          <p className="section-subtitle">{tx.categoriesSub}</p>
        </div>
        {loading ? <Spin size="large" style={{ display: 'block', margin: '40px auto' }} /> : (
          <>
            <Row gutter={[20, 20]} justify="center">
              {categories.map(cat => (
                <Col xs={12} sm={8} md={6} lg={4} key={cat.id}>
                  <Card className="category-card" hoverable onClick={() => navigate(`/categories/${cat.slug}`)}>
                    <div className="category-icon"><CategoryIcon category={cat} /></div>
                    <div className="category-name" style={{ fontWeight: 800, fontSize: 16, color: 'var(--primary)' }}>{lang === 'ar' ? cat.name_ar : cat.name_en}</div>
                  </Card>
                </Col>
              ))}
            </Row>
            <div style={{ textAlign: 'center', marginTop: 28 }}>
              <Button onClick={() => navigate('/categories')} size="large" style={{ borderRadius: 50, height: 44, paddingInline: 28, fontWeight: 700 }}>
                {tx.viewAll}
              </Button>
            </div>
          </>
        )}
      </div>

      <FeaturedFreelancers lang={lang} />
      <HowItWorks lang={lang} />

      {/* ══ Why Section ══ */}
      <div className="content-container" style={{ paddingTop: 10 }}>
        <div className="section-header">
          <h2 className="section-title">{tx.why}</h2>
          <p className="section-subtitle">{tx.whySub}</p>
        </div>
        <Row gutter={[24, 24]} justify="center">
          {tx.whyItems.map((item, i) => {
            const Icon = item.Icon;
            return (
              <Col xs={24} sm={12} md={6} key={i}>
                <Card className="why-card" hoverable>
                  <div className="why-icon"><Icon /></div>
                  <h3 style={{ fontWeight: 800, marginBottom: 10, color: 'var(--primary)', fontSize: 17 }}>{item.title}</h3>
                  <p style={{ color: 'var(--text-light)', margin: 0, lineHeight: 1.8, fontSize: 14 }}>{item.desc}</p>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>

      {/* ══ CTA Section (guests only) ══ */}
      {!user && (
      <div className="cta-section">
        <h2>{tx.ctaTitle}</h2>
        <p>{tx.ctaSub}</p>
        <Button type="primary" size="large" onClick={() => navigate('/register')}
          style={{ height: 52, paddingInline: 48, fontSize: 18, fontWeight: 800, borderRadius: 50, background: 'var(--accent-mid)', borderColor: 'var(--accent-mid)' }}>
          {tx.ctaBtn} {lang === 'ar' ? <ArrowLeftOutlined /> : <ArrowRightOutlined />}
        </Button>
      </div>
      )}
    
</div>
  );
};

export default Home;
